import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import { useKindeAuth } from '@kinde-oss/kinde-auth-react'

import type { Meta } from '../../../generated/frontend'
import { getQueryString, isLocal, Loader } from '../../../shared'
import { useBaseMetaTags } from '../../../shared/hooks/useBaseMetaTags'
import { paywallUrls } from '../../../shared/paywall-urls'
import { accountPagesDataLayer, getIsValidReferrer, setTransactionName } from '../utils'

const LoginPage: FunctionComponent = () => {
  const kinde = useKindeAuth()

  const [paywallState] = useState(paywallUrls())
  const { getBaseMetaTags } = useBaseMetaTags()

  setTransactionName('account/login')

  const metaTagObj = {
    canonical: 'https://www.which.co.uk/login',
    title: "Login to which.co.uk | Discover Best Buys & Don't Buys",
    fbAppId: '320949621749168',
    desc: 'From this page members can log directly into which.co.uk. Access thousands of expertly tested independent reviews to help you make confident choices. Please have your email address or username and password ready.',
  }

  const getMetaTags = ({ meta, imageUrl, twitterCard }: LoginMetaArgs) => [
    ...getBaseMetaTags({ meta, imageUrl, twitterCard }),
  ]

  useEffect(() => {
    const fetchKindeRedirectUrl = () => {
      let kindeRedirectUrl = window.origin
      const maybeReturnUrl = getQueryString('return_url')
      const returnUrlParamPresent = typeof maybeReturnUrl === 'string'

      const maybeReferrerPath = getQueryString('referrer_path')
      const referrerPathParamPresent = typeof maybeReferrerPath === 'string'
      const isValidReferrer = getIsValidReferrer(document?.referrer)
      if (returnUrlParamPresent) {
        kindeRedirectUrl = `${location.origin}/${maybeReturnUrl}`
      } else if (isValidReferrer && referrerPathParamPresent) {
        const referrerUrl = new URL(document.referrer)
        kindeRedirectUrl = `${referrerUrl.origin}/${maybeReferrerPath}`
      } else if (isValidReferrer) {
        kindeRedirectUrl = document.referrer
      }

      return kindeRedirectUrl
    }
    const kindeLogin = () => {
      const domain = isLocal() ? 'localhost' : '.which.co.uk'

      if (!kinde.isAuthenticated) {
        document.cookie = `sessionId=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
        document.cookie = `blaize_session=temp;path=/;domain=${domain};expires=Fri, 31 Dec 9999 23:59:59 GMT`
      }

      sessionStorage.setItem('kindeRedirect', fetchKindeRedirectUrl())
      const redirectUri =
        typeof window !== 'undefined' && window.location?.origin
          ? `${window.location.origin}/login-redirect`
          : String(paywallState.redirectFeatureUri || '')
      kinde.login({
        redirectURL: redirectUri,
      })
    }
    if (kinde && !kinde.isLoading) {
      kindeLogin()
    }
  }, [paywallState, kinde, kinde.isLoading])

  return (
    <>
      <Helmet>
        {getMetaTags({
          meta: metaTagObj,
          imageUrl: 'https://www.which.co.uk/login',
          twitterCard: 'summary_large_image',
        })}
        <script>
          {accountPagesDataLayer({
            content_type: 'login',
          })}
        </script>
      </Helmet>
      <Loader />
    </>
  )
}

export default LoginPage

/////// IMPLEMENTATION /////////

type LoginMetaArgs = {
  meta: Partial<Meta>
  imageUrl: string
  twitterCard: string
}
